import { MutationType, ShapeType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import { DataType, DataTypeUtils, PAGE_DATA_TYPES } from '../../data';
import { Rule } from '../base/mutation-rules';
import { BaseMutation, type MaybeError } from '../base/base-mutation';
import type { ITableData, IShapeData } from '../../data';
import type { PresentationDataModel } from '../../model/presentation-data-model';
import type { IValidAddShapeMutationData, IValidRemoveShapeMutationData } from '../base/valid-mutation-data';

export class AddShapeMutation extends BaseMutation<PresentationDataModel, IValidAddShapeMutationData> {
    public readonly type: MutationType = MutationType.AddShape;

    public apply(presentation: PresentationDataModel) {
        const { shapeId, pageId, shapeType, tableOptions } = this.data;
        let { parentId, index } = this.data;

        const shapeTree = presentation.store.models.get(pageId, ...PAGE_DATA_TYPES)?.store.getShapeTree();
        if (!parentId && shapeTree) {
            parentId = shapeTree.id;
            index = shapeTree.shapeList.length;
        }

        const groupShape = presentation.store.models.get(parentId, DataType.GroupShape);
        assert(groupShape, 'group shape should not exist');

        if (shapeType === ShapeType.GraphicalObjectFrameTable) {
            const { rowCount = 1, colCount = 1 } = tableOptions ?? {};

            const tableData: ITableData = {
                id: shapeId,
                type: DataType.GraphicalObjectFrameTable,
                nvPr: {},
                spPr: {},
                graphic: {
                    graphicData: {
                        tbl: {
                            tblGrid: {
                                gridCol: Array.from({ length: colCount }, () => ({ w: 0 })),
                            },
                            tr: Array.from({ length: rowCount }, () => ({
                                h: 0,
                                // 默认补充出 txBody
                                tc: Array.from({ length: colCount }, () => ({ txBody: { bodyPr: {}, p: [] } })),
                            })),
                        },
                    },
                },
            };
            groupShape.store.addShape(tableData, index);
            return;
        }
        if (shapeType === ShapeType.Shape) {
            const shapeData: IShapeData = {
                id: shapeId,
                type: DataType.Shape,
                nvPr: {},
                spPr: {},
                // 默认补充出 txBody
                txBody: { bodyPr: {}, p: [] },
            };
            groupShape.store.addShape<DataType.Shape>(shapeData, index);
            return;
        }

        if (shapeType === ShapeType.Equation) {
            const shapeData: IShapeData = {
                id: shapeId,
                type: DataType.Shape,
                nvPr: { isEquationShape: true },
                spPr: {},
                // 默认补充出 txBody
                txBody: { bodyPr: {}, p: [] },
            };
            groupShape.store.addShape<DataType.Shape>(shapeData, index);
            return;
        }

        groupShape.store.addShape(
            {
                id: shapeId,
                type: DataTypeUtils.fromShapeType(shapeType),
                nvPr: {},
                spPr: {},
            },
            index,
        );
    }

    public invert(): [IValidRemoveShapeMutationData] {
        return [
            {
                type: MutationType.RemoveShape,
                pageId: this.data.pageId,
                shapeId: this.data.shapeId,
                parentId: this.data.parentId,
                index: this.data.index,
            },
        ];
    }

    public check(presentation: PresentationDataModel): MaybeError {
        const { pageId, shapeId } = this.data;
        let { parentId, index } = this.data;

        const shapeTree = presentation.store.models.get(pageId, ...PAGE_DATA_TYPES)?.store.getShapeTree();
        if (!parentId && shapeTree) {
            parentId = shapeTree.id;
            index = shapeTree.shapeList.length;
        }

        return Rule.every(
            () => Rule.model.should.notExist(presentation, shapeId),
            () => Rule.shape.should.existInPage(presentation, parentId, pageId),
            () => Rule.shape.is.typeOf(presentation, parentId, DataType.GroupShape),
            () => Rule.groupShape.can.insertShapeToPosition(presentation, parentId, index, shapeId),
        );
    }
}
